import type {
  AvoidFeature,
  LatLng,
  RouteOptions,
  RoutePreference,
  RouteResult,
  ViewMode,
} from './types';
import { DEFAULT_ROUTE_OPTIONS } from './types';

/**
 * localStorage key under which we persist the user's last successfully
 * resolved GPS fix as `{ lat, lng }`. No TTL — the user can clear it
 * themselves via browser settings.
 */
const KEY = 'navi.lastLocation';

/**
 * Read the last known user location from localStorage.
 *
 * Returns `null` in any of these cases:
 *  - non-browser runtime (SSR / Node test).
 *  - the key is missing.
 *  - the stored JSON is malformed.
 *  - either coordinate is non-finite / wrong shape.
 *  - any access is blocked by the browser (private mode, sandboxed
 *    iframe, quota exceeded on first attempt, …).
 */
export function loadSavedLocation(): LatLng | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { lat?: unknown; lng?: unknown };
    if (
      typeof parsed.lat === 'number' &&
      typeof parsed.lng === 'number' &&
      Number.isFinite(parsed.lat) &&
      Number.isFinite(parsed.lng)
    ) {
      return { lat: parsed.lat, lng: parsed.lng };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist the user's latest GPS fix. Errors are intentionally swallowed —
 * a failed write must not break navigation; the map simply forgets and will
 * re-prompt for a fresh fix next session.
 */
export function saveLocation(loc: LatLng): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(loc));
  } catch {
    /* swallow quota / private-mode errors */
  }
}

/**
 * Persisted view-mode preference. Corrupt / missing → 'north'.
 * One-shot: pre-3-state builds stored north-up as '2d' → migrate to 'north'.
 */
const VIEW_KEY = 'navi.viewMode';
const VIEW_MIGRATED = 'navi.viewMode.v3';

export function loadSavedViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'north';
  try {
    const migrated = window.localStorage.getItem(VIEW_MIGRATED) === '1';
    const raw = window.localStorage.getItem(VIEW_KEY);
    if (!migrated) {
      window.localStorage.setItem(VIEW_MIGRATED, '1');
      if (raw === 'tilted') return 'tilted';
      return 'north';
    }
    if (raw === 'north' || raw === '2d' || raw === 'tilted') return raw;
    return 'north';
  } catch {
    return 'north';
  }
}

export function saveViewMode(mode: ViewMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIEW_MIGRATED, '1');
    window.localStorage.setItem(VIEW_KEY, mode);
  } catch {
    /* swallow */
  }
}

const ROUTE_OPTS_KEY = 'navi.routeOptions';
const AVOID_OK: ReadonlySet<string> = new Set(['highways', 'ferries', 'tollways']);
const PREF_OK: ReadonlySet<string> = new Set(['recommended', 'fastest', 'shortest']);

export function loadSavedRouteOptions(): RouteOptions {
  if (typeof window === 'undefined') return { ...DEFAULT_ROUTE_OPTIONS, avoid: [] };
  try {
    const raw = window.localStorage.getItem(ROUTE_OPTS_KEY);
    if (!raw) return { ...DEFAULT_ROUTE_OPTIONS, avoid: [] };
    const parsed = JSON.parse(raw) as {
      preference?: unknown;
      avoid?: unknown;
    };
    const preference: RoutePreference = PREF_OK.has(String(parsed.preference))
      ? (parsed.preference as RoutePreference)
      : DEFAULT_ROUTE_OPTIONS.preference;
    const avoid: AvoidFeature[] = Array.isArray(parsed.avoid)
      ? parsed.avoid.filter((v): v is AvoidFeature => AVOID_OK.has(String(v)))
      : [];
    return { preference, avoid };
  } catch {
    return { ...DEFAULT_ROUTE_OPTIONS, avoid: [] };
  }
}

export function saveRouteOptions(opts: RouteOptions): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      ROUTE_OPTS_KEY,
      JSON.stringify({
        preference: opts.preference,
        avoid: opts.avoid,
      }),
    );
  } catch {
    /* swallow */
  }
}

export interface SavedPlace {
  lat: number;
  lng: number;
  /** Address / geocode label (source of truth for the place). */
  label: string;
  /** Optional user-chosen display name for saved favorites. */
  name?: string;
}

export interface FavoritesState {
  home: SavedPlace | null;
  work: SavedPlace | null;
  recent: SavedPlace[];
  saved: SavedPlace[];
}

const FAV_KEY = 'navi.favorites';
const RECENT_MAX = 5;
const SAVED_MAX = 20;

function emptyFavorites(): FavoritesState {
  return { home: null, work: null, recent: [], saved: [] };
}

function placeKey(place: SavedPlace): string {
  return `${place.lat.toFixed(5)},${place.lng.toFixed(5)}`;
}

function parsePlace(value: unknown): SavedPlace | null {
  if (!value || typeof value !== 'object') return null;
  const o = value as { lat?: unknown; lng?: unknown; label?: unknown; name?: unknown };
  if (
    typeof o.lat !== 'number' ||
    typeof o.lng !== 'number' ||
    !Number.isFinite(o.lat) ||
    !Number.isFinite(o.lng)
  ) {
    return null;
  }
  const label =
    typeof o.label === 'string' && o.label.trim()
      ? o.label.trim()
      : `${o.lat.toFixed(5)}, ${o.lng.toFixed(5)}`;
  const name =
    typeof o.name === 'string' && o.name.trim() ? o.name.trim() : undefined;
  return name ? { lat: o.lat, lng: o.lng, label, name } : { lat: o.lat, lng: o.lng, label };
}

export function placeDisplayName(place: SavedPlace): string {
  if (place.name?.trim()) return place.name.trim();
  return place.label.split(',')[0]?.trim() || place.label;
}

export function loadFavorites(): FavoritesState {
  if (typeof window === 'undefined') return emptyFavorites();
  try {
    const raw = window.localStorage.getItem(FAV_KEY);
    if (!raw) return emptyFavorites();
    const parsed = JSON.parse(raw) as {
      home?: unknown;
      work?: unknown;
      recent?: unknown;
      saved?: unknown;
    };
    const recent = Array.isArray(parsed.recent)
      ? parsed.recent.map(parsePlace).filter((p): p is SavedPlace => p !== null).slice(0, RECENT_MAX)
      : [];
    const saved = Array.isArray(parsed.saved)
      ? parsed.saved.map(parsePlace).filter((p): p is SavedPlace => p !== null).slice(0, SAVED_MAX)
      : [];
    return {
      home: parsePlace(parsed.home),
      work: parsePlace(parsed.work),
      recent,
      saved,
    };
  } catch {
    return emptyFavorites();
  }
}

function writeFavorites(state: FavoritesState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAV_KEY, JSON.stringify(state));
  } catch {
    /* swallow */
  }
}

export function setFavoriteSlot(
  slot: 'home' | 'work',
  place: SavedPlace | null,
): FavoritesState {
  const next = loadFavorites();
  next[slot] = place;
  writeFavorites(next);
  return next;
}

export function addSavedPlace(place: SavedPlace): FavoritesState {
  const next = loadFavorites();
  const key = placeKey(place);
  next.saved = [
    place,
    ...next.saved.filter((p) => placeKey(p) !== key),
  ].slice(0, SAVED_MAX);
  writeFavorites(next);
  return next;
}

export function renameSavedPlace(place: SavedPlace, name: string | null): FavoritesState {
  const next = loadFavorites();
  const key = placeKey(place);
  const cleaned = name?.trim() ? name.trim() : undefined;
  next.saved = next.saved.map((p) => {
    if (placeKey(p) !== key) return p;
    if (cleaned) return { ...p, name: cleaned };
    return { lat: p.lat, lng: p.lng, label: p.label };
  });
  writeFavorites(next);
  return next;
}

export function removeSavedPlace(place: SavedPlace): FavoritesState {
  const next = loadFavorites();
  const key = placeKey(place);
  next.saved = next.saved.filter((p) => placeKey(p) !== key);
  writeFavorites(next);
  return next;
}

export function pushRecentDestination(place: SavedPlace): FavoritesState {
  const next = loadFavorites();
  const key = placeKey(place);
  next.recent = [
    place,
    ...next.recent.filter((r) => placeKey(r) !== key),
  ].slice(0, RECENT_MAX);
  writeFavorites(next);
  return next;
}

export interface CachedRoutePayload {
  destination: SavedPlace;
  route: {
    coords: LatLng[];
    distance: number;
    duration: number;
    steps: RouteResult['steps'];
  };
  profile: string;
  savedAt: number;
}

const LAST_ROUTE_KEY = 'navi.lastRoute';

export function saveLastRoute(payload: CachedRoutePayload): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LAST_ROUTE_KEY, JSON.stringify(payload));
  } catch {
    /* swallow quota */
  }
}

export function loadLastRoute(): CachedRoutePayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LAST_ROUTE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedRoutePayload;
    const dest = parsePlace(parsed.destination);
    if (!dest || !parsed.route || !Array.isArray(parsed.route.coords)) return null;
    if (parsed.route.coords.length < 2) return null;
    return {
      destination: dest,
      route: parsed.route,
      profile: typeof parsed.profile === 'string' ? parsed.profile : 'driving-car',
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function clearLastRoute(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LAST_ROUTE_KEY);
  } catch {
    /* swallow */
  }
}

/** Persisted multi-profile plans for the same trip (survive reload / return). */
export interface PlanRouteCacheEntry {
  key: string;
  start: LatLng;
  route: RouteResult;
  savedAt: number;
}

const PLAN_ROUTE_CACHE_KEY = 'navi.planRouteCache';
const PLAN_ROUTE_CACHE_MAX = 6;
const PLAN_ROUTE_CACHE_TTL_MS = 48 * 60 * 60 * 1000;
const PLAN_ROUTE_COORDS_MAX = 4_000;

function isFiniteLatLng(p: unknown): p is LatLng {
  if (!p || typeof p !== 'object') return false;
  const rec = p as Record<string, unknown>;
  return Number.isFinite(rec.lat) && Number.isFinite(rec.lng);
}

export function loadPlanRouteCache(): PlanRouteCacheEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PLAN_ROUTE_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { entries?: unknown };
    if (!Array.isArray(parsed.entries)) return [];
    const now = Date.now();
    const out: PlanRouteCacheEntry[] = [];
    for (const item of parsed.entries) {
      if (!item || typeof item !== 'object') continue;
      const rec = item as Record<string, unknown>;
      if (typeof rec.key !== 'string' || !rec.key) continue;
      if (!isFiniteLatLng(rec.start)) continue;
      const savedAt = typeof rec.savedAt === 'number' ? rec.savedAt : 0;
      if (savedAt > 0 && now - savedAt > PLAN_ROUTE_CACHE_TTL_MS) continue;
      const route = rec.route as RouteResult | undefined;
      if (!route || !Array.isArray(route.coords) || route.coords.length < 2) continue;
      if (!Array.isArray(route.steps)) continue;
      if (!Number.isFinite(route.distance) || !Number.isFinite(route.duration)) continue;
      out.push({
        key: rec.key,
        start: { lat: Number(rec.start.lat), lng: Number(rec.start.lng) },
        route: {
          coords: route.coords,
          distance: route.distance,
          duration: route.duration,
          steps: route.steps,
        },
        savedAt,
      });
      if (out.length >= PLAN_ROUTE_CACHE_MAX) break;
    }
    return out;
  } catch {
    return [];
  }
}

export function savePlanRouteCache(entries: PlanRouteCacheEntry[]): void {
  if (typeof window === 'undefined') return;
  try {
    const slim = entries.slice(-PLAN_ROUTE_CACHE_MAX).map((e) => ({
      key: e.key,
      start: e.start,
      savedAt: e.savedAt,
      route: {
        coords: thinCoordsForPlanCache(e.route.coords),
        distance: e.route.distance,
        duration: e.route.duration,
        steps: e.route.steps.slice(0, 400),
      },
    }));
    window.localStorage.setItem(
      PLAN_ROUTE_CACHE_KEY,
      JSON.stringify({ entries: slim }),
    );
  } catch {
    /* quota / private mode — drop oldest and retry once */
    try {
      const slim = entries.slice(-2).map((e) => ({
        key: e.key,
        start: e.start,
        savedAt: e.savedAt,
        route: {
          coords: thinCoordsForPlanCache(e.route.coords),
          distance: e.route.distance,
          duration: e.route.duration,
          steps: e.route.steps.slice(0, 200),
        },
      }));
      window.localStorage.setItem(
        PLAN_ROUTE_CACHE_KEY,
        JSON.stringify({ entries: slim }),
      );
    } catch {
      /* give up */
    }
  }
}

export function clearPlanRouteCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PLAN_ROUTE_CACHE_KEY);
  } catch {
    /* swallow */
  }
}

function thinCoordsForPlanCache(coords: LatLng[]): LatLng[] {
  if (coords.length <= PLAN_ROUTE_COORDS_MAX) return coords;
  const last = coords.length - 1;
  const step = last / (PLAN_ROUTE_COORDS_MAX - 1);
  const out: LatLng[] = new Array(PLAN_ROUTE_COORDS_MAX);
  for (let i = 0; i < PLAN_ROUTE_COORDS_MAX; i++) {
    const idx = i === PLAN_ROUTE_COORDS_MAX - 1 ? last : Math.round(i * step);
    out[i] = coords[idx]!;
  }
  return out;
}

const POWER_SAVER_KEY = 'navi.powerSaver';

export function loadSavedPowerSaver(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(POWER_SAVER_KEY) === '1';
  } catch {
    return false;
  }
}

export function savePowerSaver(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(POWER_SAVER_KEY, on ? '1' : '0');
  } catch {
    /* swallow */
  }
}
